import { useState, useEffect } from 'react';
import { ColumnsType, DeleteIcon, EditIcon } from '@/components/_common';
import {
  NumberingsForm,
  NumberingsRequest,
  NumberingsResponse,
} from '@/core/domain/invoice/Numberings/Numberings.types';
import { useDisclosure } from '@/hooks';
import { Button, Col, Divider, Form, Modal, Row, Space, Table } from 'antd';
import {
  useCreateNumberings,
  useDeleteNumberings,
  useNumberingsMutation,
  useUpdateNumberings,
} from '@/core/domain/invoice/Numberings/Numberings.hook';
import SerieNumberModalForm from './SerieNumberModalForm';

interface Props {
  configNfe?: number;
  configNfc?: number;
  configNfs?: number;
}

const InvoiceSerieNumberFields = ({
  configNfe,
  configNfc,
  configNfs,
}: Props) => {
  const [form] = Form.useForm<NumberingsForm>();

  const createNumberings = useCreateNumberings();
  const deleteNumberings = useDeleteNumberings();
  const updateNumberings = useUpdateNumberings();

  const { mutateAsync, data, isLoading } = useNumberingsMutation();

  const handleGetAll = async () => {
    if (configNfe) {
      mutateAsync({ configNfeId: configNfe ?? undefined });
    }

    if (configNfc) {
      mutateAsync({ configNfcId: configNfc ?? undefined });
    }

    if (configNfs) {
      mutateAsync({ configNfsId: configNfs ?? undefined });
    }
  };

  useEffect(() => {
    handleGetAll();
  }, [configNfe]);

  const [isEdit, setIsEdit] = useState<number>();

  const [modalVisible, modalVisibleAction] = useDisclosure();

  const columns: ColumnsType<NumberingsResponse> = [
    {
      dataIndex: 'series',
      title: 'Série',
      width: '33%',
    },
    {
      dataIndex: 'initialNumber',
      title: configNfs ? 'Numeração inicial RPS' : 'Numeração inicial',
      width: '33%',
    },
    {
      dataIndex: 'main',
      title: 'Principal',
      width: '33%',
      render: (main: boolean) => {
        return main ? 'Sim' : 'Não';
      },
    },
    {
      key: 'operation',
      width: '8%',
      fixed: 'right',
      align: 'right',
      render: (_, row) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => {
                setIsEdit(row.id);
                modalVisibleAction.toggle();
              },
            }}
          />

          <Divider type="vertical" />

          <DeleteIcon
            disabled={row.main}
            onClick={() => {
              Modal.confirm({
                title: 'Deseja realmente excluir a série e a numeração?',
                content: `Ao remover a série e a numeração, a recuperação do mesmo não poderá ser feita. Ainda sim deseja continuar?`,
                onOk: async () => {
                  await deleteNumberings.mutateAsync(row.id);
                  await handleGetAll();
                },
                okType: 'danger',
              });
            }}
          />
        </Space>
      ),
    },
  ];

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (values) {
        const obj: NumberingsRequest = {
          ...values,
          configNfcId: configNfc,
          configNfeId: configNfe,
          configNfsId: configNfs,
        };

        if (isEdit) {
          await updateNumberings.mutateAsync({ ...obj, id: isEdit });
        } else {
          await createNumberings.mutateAsync(obj);
        }

        await handleGetAll();
        setIsEdit(undefined);
        modalVisibleAction.toggle();
      }
    } catch {}
  };

  return (
    <Form form={form} preserve={false}>
      <Row justify="end">
        <Col>
          <Button type="primary" onClick={() => modalVisibleAction.toggle()}>
            Cadastrar série
          </Button>
        </Col>

        <Col span={24}>
          <Table<NumberingsResponse>
            loading={isLoading}
            dataSource={data}
            columns={columns}
            pagination={false}
          />
        </Col>
      </Row>

      <Modal
        destroyOnClose
        onOk={handleSubmit}
        confirmLoading={
          createNumberings.isLoading || updateNumberings.isLoading || isLoading
        }
        open={modalVisible}
        title="Série e Numeração"
        onCancel={() => {
          setIsEdit(undefined);
          modalVisibleAction.toggle();
        }}
      >
        <SerieNumberModalForm
          form={form}
          isEdit={isEdit}
          configNfs={configNfs}
        />
      </Modal>
    </Form>
  );
};

export default InvoiceSerieNumberFields;
